import { Tag } from 'antd';
import generatePicker from 'antd/es/date-picker/generatePicker';
import classNames from 'classnames';
import React, { useState } from 'react';

import { Select } from 'app/components/fields/Select';
import { dateGenerateConfig } from 'app/components/fields/dateGenerateConfig';
import { addDays, format, isAfter, isBefore, subDays } from 'app/utils/dayjs';

import './MultipleDatePicker.css';

type Value = string[] | undefined;

type MultipleDatePickerProps = {
  value?: Value;
  onChange?: (value: Value) => void;
  disabled?: boolean;
  disableAfter?: Date;
};

const DatePicker = generatePicker<Date>(dateGenerateConfig);

export const MultipleDatePicker = ({
  onChange,
  value,
  disabled = false,
  disableAfter = addDays(new Date(), 60),
}: MultipleDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const [arrayDateSelect, setArrayDateSelect] = useState<string[]>(value || []);

  if (!onChange) {
    return null;
  }

  const onValueChange = (date: Date | null) => {
    if (date === null) {
      return;
    }

    let arrayDates = arrayDateSelect;
    const formatDate = format(date, 'YYYY-MM-DD');

    const index = arrayDates.indexOf(formatDate);

    if (index > -1) {
      arrayDates = arrayDates.filter((item) => item !== formatDate);
    } else {
      arrayDates = [...arrayDates, formatDate];
    }

    arrayDates = arrayDates.sort();
    setArrayDateSelect(arrayDates);

    onChange(arrayDates);
  };

  const dateRender = (currentDate: Date) => {
    const arrayDates = arrayDateSelect;

    const t = format(currentDate, 'YYYY-MM-DD');
    const isSelected = arrayDates.indexOf(t) > -1;

    return (
      <div
        className={classNames(
          {
            dateSelected: isSelected,
          },
          'ant-picker-cell-inner'
        )}
      >
        {format(currentDate, 'DD')}
      </div>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const handleClose = (value: string) => {
    let arrayDates = arrayDateSelect;

    arrayDates = arrayDates.filter((item) => item !== value);
    setArrayDateSelect(arrayDates);
    onChange(arrayDates);
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const renderTag = ({ value }: { value: string }) => {
    return (
      <Tag
        closable={!disabled}
        className={disabled ? 'date-tag-disabled' : ''}
        onClose={() => handleClose(value)}
      >
        {format(new Date(value), 'ddd DD MMM')}
      </Tag>
    );
  };

  return (
    <Select
      useDefaultPopupContainer={false}
      allowClear={!disabled}
      placeholder={'Select few dates'}
      mode="multiple"
      value={arrayDateSelect}
      tagRender={renderTag}
      open={open}
      onClear={() => setArrayDateSelect([])}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      dropdownMatchSelectWidth={false}
      popupClassName={`multiple-dropdown`}
      dropdownStyle={{ height: '270px', width: '280px', minWidth: '0' }}
      disabled={disabled}
      dropdownRender={() => {
        return (
          <DatePicker
            // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
            format={(value) => ''}
            onChange={onValueChange}
            open={open}
            value={undefined}
            dateRender={dateRender}
            disabledDate={(d) =>
              !d ||
              isAfter(d, disableAfter) ||
              isBefore(d, subDays(new Date(), 1))
            }
            style={{ visibility: 'hidden' }}
            getPopupContainer={(node) =>
              (node.parentNode || node) as HTMLElement
            }
          />
        );
      }}
    />
  );
};
